// /**
//  * Updates the recently viewed products in localStorage.
//  */
// export class RecentlyViewed {
//   /** @static @constant {string} The key used to store the viewed products in session storage */
//   static #STORAGE_KEY = 'viewedProducts';
//   /** @static @constant {number} The maximum number of products to store */
//   static #MAX_PRODUCTS = 4;

//   /**
//    * Adds a product to the recently viewed products list.
//    * @param {string} productId - The ID of the product to add.
//    */
//   static addProduct(productId) {
//     let viewedProducts = this.getProducts();

//     viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
//     viewedProducts.unshift(productId);
//     viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

//     localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
//   }

//   static clearProducts() {
//     localStorage.removeItem(this.#STORAGE_KEY);
//   }

//   /**
//    * Retrieves the list of recently viewed products from session storage.
//    * @returns {string[]} The list of viewed products.
//    */
//   static getProducts() {
//     return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
//   }
// }



import { sectionRenderer } from '@theme/section-renderer';

/**
 * Keeps track of recently viewed product IDs in localStorage.
 * Also dispatches browser events so other components (search, sections) can update.
 */
export class RecentlyViewed {
  static #STORAGE_KEY = 'viewedProducts';
  static #MAX_PRODUCTS = 12;
  static STORAGE_UPDATED_EVENT = 'recently-viewed:updated';

  /**
   * Adds a product ID (or object containing an ID) to the recently viewed list.
   * @param {string | number | { id: string | number }} product
   */
  static addProduct(product) {
    const productId = this.#normalizeProductId(product);
    if (!productId) return;

    let viewedProducts = this.getProducts().filter((id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (error) {
      console.warn('Unable to persist recently viewed products', error);
    }

    this.#dispatchUpdate(viewedProducts);
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch (error) {
      console.warn('Unable to clear recently viewed products', error);
    }

    this.#dispatchUpdate([]);
  }

  /**
   * Retrieves the list of recently viewed products from localStorage.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    try {
      const stored = localStorage.getItem(this.#STORAGE_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((value) => this.#normalizeProductId(value))
        .filter((value) => typeof value === 'string');
    } catch (error) {
      console.warn('Unable to read recently viewed products', error);
      return [];
    }
  }

  static get storageKey() {
    return this.#STORAGE_KEY;
  }

  static get maxProducts() {
    return this.#MAX_PRODUCTS;
  }

  /**
   * Normalizes a product identifier into a string.
   * @param {unknown} product
   * @returns {string | null}
   */
  static #normalizeProductId(product) {
    if (product == null) return null;
    if (typeof product === 'object' && 'id' in product) {
      return this.#normalizeProductId(product.id);
    }
    if (typeof product === 'number' && Number.isFinite(product)) {
      return String(product);
    }
    if (typeof product === 'string') {
      return product.trim();
    }
    return null;
  }

  /**
   * Dispatches a browser event when the recently viewed list changes.
   * @param {string[]} products
   */
  static #dispatchUpdate(products) {
    document.dispatchEvent(
      new CustomEvent(this.STORAGE_UPDATED_EVENT, {
        detail: { products: [...products] },
      })
    );
  }
}

class RecentlyViewedProductsElement extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  #handleListUpdate = () => {
    this.#render();
  };

  /**
   * @param {StorageEvent} event
   */
  #handleStorageEvent = (event) => {
    if (event.key && event.key !== RecentlyViewed.storageKey) return;
    this.#render();
  };

  connectedCallback() {
    this.#render();

    document.addEventListener(RecentlyViewed.STORAGE_UPDATED_EVENT, this.#handleListUpdate);
    window.addEventListener('storage', this.#handleStorageEvent);
  }

  disconnectedCallback() {
    document.removeEventListener(RecentlyViewed.STORAGE_UPDATED_EVENT, this.#handleListUpdate);
    window.removeEventListener('storage', this.#handleStorageEvent);
    this.#activeFetch?.abort();
  }

  /**
   * Fetches the section markup for the stored product IDs.
   * @returns {Promise<void>}
   */
  async #render() {
    const sectionId = this.dataset.sectionId;
    const requestUrl = this.dataset.url;
    const limit = Number.parseInt(this.dataset.limit || '', 10) || RecentlyViewed.maxProducts;

    const productIds = RecentlyViewed.getProducts().slice(0, limit);

    if (!sectionId || !requestUrl) {
      console.warn('recently-viewed-products element requires data-section-id and data-url attributes.');
      return;
    }

    if (productIds.length === 0) {
      this.#showEmptyState();
      return;
    }

    const url = this.#buildRequestURL(requestUrl, productIds);
    if (!url) return;

    const abortController = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = abortController;

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (abortController.signal.aborted) return;

      this.#hydrateFromHTML(sectionHTML, sectionId);
      this.classList.remove('hidden');
      this.dataset.loaded = 'true';
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('Recently viewed products error:', error instanceof Error ? error.message : error);
      this.#showEmptyState();
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Replaces the element's inner HTML with the content from the fetched section.
   * @param {string} markup
   * @param {string} sectionId
   */
  #hydrateFromHTML(markup, sectionId) {
    if (!markup) {
      this.#showEmptyState();
      return;
    }

    const fragment = new DOMParser().parseFromString(markup, 'text/html');
    const updatedElement =
      fragment.getElementById(this.id) ||
      fragment.getElementById(`shopify-section-${sectionId}`)?.querySelector(`recently-viewed-products#${this.id}`);

    if (!updatedElement) {
      this.#showEmptyState();
      return;
    }

    this.innerHTML = updatedElement.innerHTML;
  }

  /**
   * Builds the search URL used to fetch recently viewed products from the backend.
   * @param {string} baseUrl
   * @param {string[]} productIds
   * @returns {URL | null}
   */
  #buildRequestURL(baseUrl, productIds) {
    try {
      const url = new URL(baseUrl, window.location.origin);
      const query = productIds.map((id) => `id:${id}`).join(' OR ');
      if (!query.length) return null;

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('recently_viewed', productIds.join(','));
      url.searchParams.sort();

      return url;
    } catch (error) {
      console.warn('Invalid recently viewed request URL:', baseUrl, error);
      return null;
    }
  }

  #showEmptyState() {
    this.dataset.loaded = 'false';
    this.classList.add('hidden');
    this.innerHTML = '';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsElement);
}
